import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Paper } from '@mui/material';
import { DefaultLayout } from '../../components/layout/default-layout';
import { moduleClientFactory } from '../../api-service/module-client-factory';
import {
  AccessibleVisitationTeam,
  GetVisitationTeamInsightsResponse,
  VisitationAgendaCaseModel,
  VisitationAgendaChange,
  VisitationAgendaModel,
  VisitationDecision,
  VisitationMeetingModel,
} from '../../api-service/clients';
import { useBroadcastContext, useMetaTitle } from '../../hooks';
import { initialAssessmentDraft, processValidationError, VisitationAssessmentDraft } from './visitation-assessment-draft';
import { meetingKey } from './visitation-meeting-events';
import { VisitationTeamPageComponent } from './visitation-team-page-component';

const client = moduleClientFactory.createVisitationTeamAgendaClient(true);

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function VisitationTeamPage() {
  useMetaTitle('Visitationsteam');
  const { enqueueSnackbar } = useSnackbar();

  const [teams, setTeams] = useState<AccessibleVisitationTeam[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>();
  const [visibleRange, setVisibleRange] = useState<{ start: Date; end: Date }>();
  const [meetings, setMeetings] = useState<VisitationMeetingModel[]>([]);
  const [selectedMeeting, setSelectedMeeting] = useState<VisitationMeetingModel>();
  const [agenda, setAgenda] = useState<VisitationAgendaModel>();
  const [drafts, setDrafts] = useState<Record<string, VisitationAssessmentDraft>>({});
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
  const [isLoadingAgenda, setIsLoadingAgenda] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [insights, setInsights] = useState<GetVisitationTeamInsightsResponse>();
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);

  useEffect(() => {
    async function loadTeams() {
      try {
        const response = await client.getAccessibleVisitationTeams();
        setTeams(response.visitationTeams);
        setSelectedTeamId(response.visitationTeams[0]?.visitationTeamId);
      } finally {
        setIsLoadingTeams(false);
      }
    }
    loadTeams();
  }, []);

  const meetingsRequestRef = useRef(0);

  async function loadMeetings(visitationTeamId: string, range: { start: Date; end: Date }, silently = false) {
    const request = ++meetingsRequestRef.current;
    if (!silently) setIsLoadingMeetings(true);
    try {
      const response = await client.getVisitationTeamMeetings({ visitationTeamId, from: toIsoDate(range.start), to: toIsoDate(range.end) });
      if (request !== meetingsRequestRef.current) return;
      setMeetings(response.meetings);
    } finally {
      setIsLoadingMeetings(false);
    }
  }

  const agendaRequestRef = useRef(0);

  async function loadAgenda(visitationTeamId: string, meeting: VisitationMeetingModel, keepDrafts = false) {
    const request = ++agendaRequestRef.current;
    if (!keepDrafts) setIsLoadingAgenda(true);
    try {
      const response = await client.getVisitationAgenda({
        visitationTeamId,
        visitationScheduleId: meeting.visitationScheduleId,
        meetingStartAt: meeting.startAt,
      });
      if (request !== agendaRequestRef.current) return;
      setAgenda(response.agenda);
      setDrafts(previous =>
        Object.fromEntries(
          response.agenda.cases.map(agendaCase => [agendaCase.visitationBookingId, (keepDrafts && previous[agendaCase.visitationBookingId]) || initialAssessmentDraft(agendaCase)])
        )
      );
    } finally {
      setIsLoadingAgenda(false);
    }
  }

  useEffect(() => {
    if (!selectedTeamId) return;
    const visitationTeamId = selectedTeamId;
    async function loadInsights() {
      setIsLoadingInsights(true);
      try {
        const today = new Date();
        setInsights(
          await client.getVisitationTeamInsights({
            visitationTeamId,
            from: toIsoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 84)),
            to: toIsoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 84)),
          })
        );
      } finally {
        setIsLoadingInsights(false);
      }
    }
    loadInsights();
  }, [selectedTeamId]);

  useEffect(() => {
    if (selectedTeamId && visibleRange) {
      loadMeetings(selectedTeamId, visibleRange);
    }
  }, [selectedTeamId, visibleRange?.start.getTime(), visibleRange?.end.getTime()]);

  function handleSelectTeam(visitationTeamId: string) {
    meetingsRequestRef.current++;
    agendaRequestRef.current++;
    setSelectedTeamId(visitationTeamId);
    setSelectedMeeting(undefined);
    setAgenda(undefined);
  }

  function handleVisibleRangeChange(range: { start: Date; end: Date }) {
    meetingsRequestRef.current++;
    setVisibleRange(range);
  }

  function handleSelectMeeting(meeting: VisitationMeetingModel) {
    if (!selectedTeamId) return;
    setSelectedMeeting(meeting);
    loadAgenda(selectedTeamId, meeting);
  }

  // Bookings change from rulings in other tabs and by other users, so the calendar must not wait for a manual reload.
  async function refreshSilently() {
    if (!selectedTeamId || isSaving) return;
    await Promise.all([
      visibleRange ? loadMeetings(selectedTeamId, visibleRange, true) : Promise.resolve(),
      selectedMeeting ? loadAgenda(selectedTeamId, selectedMeeting, true) : Promise.resolve(),
    ]);
  }

  const refreshSilentlyRef = useRef(refreshSilently);
  refreshSilentlyRef.current = refreshSilently;
  const { subscribe: onVisitationBookingChanged } = useBroadcastContext('VisitationBookingChanged');

  useEffect(() => {
    onVisitationBookingChanged(() => refreshSilentlyRef.current());
    const handleFocus = () => refreshSilentlyRef.current();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function reloadAfterChange() {
    if (!selectedTeamId || !selectedMeeting) return;
    await Promise.all([loadAgenda(selectedTeamId, selectedMeeting), visibleRange ? loadMeetings(selectedTeamId, visibleRange) : Promise.resolve()]);
  }

  async function changeAgenda(change: VisitationAgendaChange, successMessage: string) {
    if (!selectedTeamId || !selectedMeeting) return;
    setIsSaving(true);
    try {
      await client.changeVisitationAgenda({
        meeting: { visitationTeamId: selectedTeamId, visitationScheduleId: selectedMeeting.visitationScheduleId, meetingStartAt: selectedMeeting.startAt },
        change,
      });
      enqueueSnackbar(successMessage, { variant: 'success' });
      await reloadAfterChange();
    } finally {
      setIsSaving(false);
    }
  }

  function handleCloseAgenda() {
    changeAgenda(VisitationAgendaChange.Close, 'Dagsordenen er lukket');
  }

  function handleReopenAgenda() {
    changeAgenda(VisitationAgendaChange.Reopen, 'Dagsordenen er genåbnet');
  }

  function handleCompressAgenda() {
    changeAgenda(VisitationAgendaChange.Compress, 'Sagerne er rykket sammen');
  }

  async function handleCancelMeeting() {
    if (!selectedTeamId || !selectedMeeting) return;
    setIsSaving(true);
    try {
      await client.cancelVisitationMeeting({
        meeting: { visitationTeamId: selectedTeamId, visitationScheduleId: selectedMeeting.visitationScheduleId, meetingStartAt: selectedMeeting.startAt },
      });
      enqueueSnackbar('Mødet er aflyst', { variant: 'success' });
      await reloadAfterChange();
    } finally {
      setIsSaving(false);
    }
  }

  function updateDraft(visitationBookingId: string, change: (draft: VisitationAssessmentDraft) => VisitationAssessmentDraft) {
    setDrafts(previous => ({ ...previous, [visitationBookingId]: { ...change(previous[visitationBookingId]), error: undefined } }));
  }

  function handleGrantDecisionChange(visitationBookingId: string, grantId: string, decision: VisitationDecision) {
    updateDraft(visitationBookingId, draft => ({ ...draft, grants: { ...draft.grants, [grantId]: { ...draft.grants[grantId], decision } } }));
  }

  function handleGrantCommentChange(visitationBookingId: string, grantId: string, comment: string) {
    updateDraft(visitationBookingId, draft => ({ ...draft, grants: { ...draft.grants, [grantId]: { ...draft.grants[grantId], comment } } }));
  }

  function handleReasoningChange(visitationBookingId: string, reasoning: string) {
    updateDraft(visitationBookingId, draft => ({ ...draft, reasoning }));
  }

  async function registerAssessment(agendaCase: VisitationAgendaCaseModel, postpone: boolean) {
    if (!selectedTeamId) return;
    const draft = drafts[agendaCase.visitationBookingId];
    const error = postpone ? (draft.reasoning.trim() ? undefined : 'Skriv, hvorfor sagen udsættes.') : processValidationError(agendaCase, draft);
    if (error) {
      setDrafts(previous => ({ ...previous, [agendaCase.visitationBookingId]: { ...draft, error } }));
      return;
    }

    setIsSaving(true);
    try {
      await client.registerVisitationAssessment({
        visitationTeamId: selectedTeamId,
        visitationBookingId: agendaCase.visitationBookingId,
        rulingId: agendaCase.rulingId,
        postpone,
        reasoning: `<p>${draft.reasoning.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')}</p>`,
        reasoningPlainText: draft.reasoning,
        grants: postpone
          ? []
          : Object.entries(draft.grants).map(([grantId, grant]) => ({
              grantId,
              decision: grant.decision as VisitationDecision,
              comment: grant.comment || undefined,
            })),
      });
      enqueueSnackbar(postpone ? 'Sagen er udsat' : 'Vurderingen er registreret', { variant: 'success' });
      await reloadAfterChange();
    } finally {
      setIsSaving(false);
    }
  }

  function handleProcess(agendaCase: VisitationAgendaCaseModel) {
    registerAssessment(agendaCase, false);
  }

  function handlePostpone(agendaCase: VisitationAgendaCaseModel) {
    registerAssessment(agendaCase, true);
  }

  return (
    <DefaultLayout>
      <Box sx={{ px: 2, pt: 2 }}>
        <Paper elevation={0} sx={{ bgcolor: 'common.white', p: 2, borderRadius: 1 }}>
          <VisitationTeamPageComponent
            teams={teams}
            selectedTeamId={selectedTeamId}
            meetings={meetings}
            selectedMeetingKey={selectedMeeting ? meetingKey(selectedMeeting) : undefined}
            agenda={agenda}
            drafts={drafts}
            insights={insights}
            isLoadingInsights={isLoadingInsights}
            isLoadingTeams={isLoadingTeams}
            isLoadingMeetings={isLoadingMeetings}
            isLoadingAgenda={isLoadingAgenda}
            isSaving={isSaving}
            onSelectTeam={handleSelectTeam}
            onVisibleRangeChange={handleVisibleRangeChange}
            onSelectMeeting={handleSelectMeeting}
            onCloseAgenda={handleCloseAgenda}
            onReopenAgenda={handleReopenAgenda}
            onCompressAgenda={handleCompressAgenda}
            onCancelMeeting={handleCancelMeeting}
            onGrantDecisionChange={handleGrantDecisionChange}
            onGrantCommentChange={handleGrantCommentChange}
            onReasoningChange={handleReasoningChange}
            onProcess={handleProcess}
            onPostpone={handlePostpone}
          />
        </Paper>
      </Box>
    </DefaultLayout>
  );
}
